import { AgentState, Call, Disposition } from '~services/AsteriskDiallerManager/domain';

export type SelectOption = { value: string; label: string };

export type AgentStateEntry = {
  value: string;
  label: string;
  status: string;
};

export type DiallerState = {
  // Connection
  connected: boolean;

  // Agent state
  state: string;
  pendingState: string;
  stateTime: string;
  username: string;

  // Campaign
  campaignInformation: {
    name: string;
    description: string;
    agentStatuses: string[];
  };

  // Call
  call: Call | null;
  callType: string;
  isInbound: boolean;
  pendingPreviewCall: Call | null;
  offeredCall: Call | null;

  // Disposition
  dispositions: Disposition[];
  exclusionLists: SelectOption[];
  currentDispo: Disposition | null;
  currentExclusionList: SelectOption | null;
  callbackEndpoint: string;
  callbackToAgent: boolean;
  callbackTime: string | null;
  callbackNotes: string;
  leadEndpoint: string;

  // Agent presence (other agents)
  agentStates: AgentStateEntry[];

  // Server-driven capabilities
  canCallPreview: boolean;
  canDispo: boolean;
  agentMuted: boolean;

  // Anonymous caller fields (customerNum === 0)
  newLeadName: string;
  newLeadTimezone: SelectOption | null;
  newLeadExternalId: string;

  // Amplifon fields (App.svelte:827-829)
  currentClientCode: string;
  currentHearAboutUs: SelectOption | null;

  // HCF fields (App.svelte:831-836)
  currentIsTransfer: SelectOption | null;
  currentPhoneNumber: string;
  currentMembershipID: string;
  currentInsuranceOffered: SelectOption | null;
  currentInsuranceOutcome: SelectOption | null;

  // Manual outbound
  manualOutboundOpen: boolean;
  manualOutboundNumber: string;
  manualOutboundTimezone: SelectOption | null;

  // Lazy-loaded lists
  timezoneList: SelectOption[];
};

export const initialDiallerState: DiallerState = {
  connected: false,

  state: 'Logged Out',
  pendingState: '',
  stateTime: '',
  username: '',

  campaignInformation: {
    name: '',
    description: '',
    agentStatuses: [],
  },

  call: null,
  callType: '',
  isInbound: false,
  pendingPreviewCall: null,
  offeredCall: null,

  dispositions: [],
  exclusionLists: [],
  currentDispo: null,
  currentExclusionList: null,
  callbackEndpoint: '',
  callbackToAgent: false,
  callbackTime: null,
  callbackNotes: '',
  leadEndpoint: '',

  agentStates: [],

  canCallPreview: false,
  canDispo: false,
  agentMuted: false,

  newLeadName: '',
  newLeadTimezone: null,
  newLeadExternalId: '',

  currentClientCode: '',
  currentHearAboutUs: null,
  currentIsTransfer: null,
  currentPhoneNumber: '',
  currentMembershipID: '',
  currentInsuranceOffered: null,
  currentInsuranceOutcome: null,

  manualOutboundOpen: false,
  manualOutboundNumber: '',
  manualOutboundTimezone: null,

  timezoneList: [],
};

// If no exclusion lists are configured, remove dispositions with outcome "excluded"
// to prevent agents from selecting an action they can't complete (filterDispositions in App.svelte)
const filterDispositions = (exclusionLists: SelectOption[], dispositions: Disposition[]): Disposition[] => {
  if (exclusionLists.length > 0) return dispositions;
  return dispositions.filter((d) => d.outcome !== 'excluded');
};

const determineCallType = (call: Call): string => {
  const hasInbound = Object.values(call.customerChannels).some((ch) => ch.isInbound);
  if (hasInbound) return 'Inbound';
  if (call.lead.callbackId !== null) return 'Callback';
  return 'Outbound';
};

export type DiallerAction =
  | { type: 'SET_CAMPAIGN_INFORMATION'; payload: { name: string; description: string; agentStatuses: string[] } }
  | { type: 'SET_ENTERED_STATE'; payload: { newState: string; stateTime: string; username: string } }
  | { type: 'SET_PENDING_STATE'; payload: { pendingState: string } }
  | { type: 'SET_CALL_INFORMATION'; payload: Call }
  | { type: 'SET_AGENT_PRESENCE'; payload: { [key: string]: string } }
  | { type: 'SET_AGENT_MUTED'; payload: boolean }
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_OFFERED_CALL'; payload: Call | null }
  | { type: 'SET_PENDING_PREVIEW_CALL'; payload: Call | null }
  | { type: 'SET_EXCLUSION_LISTS'; payload: string[] }
  | { type: 'SET_CURRENT_DISPO'; payload: Disposition | null }
  | { type: 'SET_CURRENT_EXCLUSION_LIST'; payload: SelectOption | null }
  | { type: 'SET_CALLBACK_ENDPOINT'; payload: string }
  | { type: 'SET_CALLBACK_TO_AGENT'; payload: boolean }
  | { type: 'SET_CALLBACK_TIME'; payload: string | null }
  | { type: 'SET_CALLBACK_NOTES'; payload: string }
  | { type: 'SET_NEW_LEAD_NAME'; payload: string }
  | { type: 'SET_NEW_LEAD_TIMEZONE'; payload: SelectOption | null }
  | { type: 'SET_NEW_LEAD_EXTERNAL_ID'; payload: string }
  | { type: 'SET_CURRENT_CLIENT_CODE'; payload: string }
  | { type: 'SET_CURRENT_HEAR_ABOUT_US'; payload: SelectOption | null }
  | { type: 'SET_CURRENT_IS_TRANSFER'; payload: SelectOption | null }
  | { type: 'SET_CURRENT_PHONE_NUMBER'; payload: string }
  | { type: 'SET_CURRENT_MEMBERSHIP_ID'; payload: string }
  | { type: 'SET_CURRENT_INSURANCE_OFFERED'; payload: SelectOption | null }
  | { type: 'SET_CURRENT_INSURANCE_OUTCOME'; payload: SelectOption | null }
  | { type: 'SET_MANUAL_OUTBOUND_OPEN'; payload: boolean }
  | { type: 'SET_MANUAL_OUTBOUND_NUMBER'; payload: string }
  | { type: 'SET_MANUAL_OUTBOUND_TIMEZONE'; payload: SelectOption | null }
  | { type: 'RESET_MANUAL_OUTBOUND' }
  | { type: 'SET_TIMEZONE_LIST'; payload: SelectOption[] };

export const diallerReducer = (state: DiallerState, action: DiallerAction): DiallerState => {
  switch (action.type) {
    case 'SET_CONNECTED': {
      if (!action.payload) {
        // Disconnected
        return {
          ...state,
          connected: false,
          state: 'Disconnected',
          pendingState: '',
          call: null,
          callType: '',
          isInbound: false,
          canDispo: false,
          canCallPreview: false,
          callbackEndpoint: '',
          leadEndpoint: '',
          callbackToAgent: false,
          callbackTime: null,
          callbackNotes: '',
          currentDispo: null,
          currentExclusionList: null,
          newLeadName: '',
          newLeadTimezone: null,
          newLeadExternalId: '',
          currentClientCode: '',
          currentHearAboutUs: null,
          currentIsTransfer: null,
          currentPhoneNumber: '',
          currentMembershipID: '',
          currentInsuranceOffered: null,
          currentInsuranceOutcome: null,
          pendingPreviewCall: null,
          offeredCall: null,
        };
      }
      return { ...state, connected: true };
    }

    case 'SET_CAMPAIGN_INFORMATION':
      return {
        ...state,
        campaignInformation: action.payload,
      };

    case 'SET_ENTERED_STATE': {
      const newState = action.payload.newState;
      // Clear call/dispo fields when leaving AfterCallWork or Unavailable (handleEnteredState in App.svelte)
      const shouldReset =
        (state.state === AgentState.AfterCallWork && newState !== AgentState.AfterCallWork) ||
        state.state === AgentState.Unavailable;

      return {
        ...state,
        state: newState,
        stateTime: action.payload.stateTime,
        username: action.payload.username,
        ...(shouldReset && {
          call: null,
          callType: '',
          isInbound: false,
          canDispo: false,
          callbackEndpoint: '',
          leadEndpoint: '',
          callbackToAgent: false,
          callbackTime: null,
          callbackNotes: '',
          currentDispo: null,
          currentExclusionList: null,
          currentClientCode: '',
          currentHearAboutUs: null,
          currentIsTransfer: null,
          currentPhoneNumber: '',
          currentMembershipID: '',
          currentInsuranceOffered: null,
          currentInsuranceOutcome: null,
        }),
      };
    }

    case 'SET_PENDING_STATE':
      return { ...state, pendingState: action.payload.pendingState };

    case 'SET_CALL_INFORMATION': {
      const isNewCall = state.call !== null && state.call.id !== action.payload.id;
      const firstEndpoint = action.payload.lead.endpoints[0] ?? '';
      const serverDispo = action.payload.disposition;
      const cb = serverDispo?.callback ?? null;

      // Find matching disposition from server-set dispo (handleCallInformation in App.svelte)
      let matchedDispo: Disposition | null = null;
      if (serverDispo) {
        matchedDispo =
          action.payload.dispositions.find((d) => d.code === serverDispo.code && d.subCode === serverDispo.subCode) ??
          null;
      }

      // Keep the existing reference if the same dispo — prevents DispositionForm useEffect infinite loop.
      const sameDispo =
        matchedDispo !== null &&
        state.currentDispo !== null &&
        matchedDispo.code === state.currentDispo.code &&
        matchedDispo.subCode === state.currentDispo.subCode;

      // canCallPreview: false if any customer channel is Connected or Ringing (handleCallInformation in App.svelte)
      const hasActiveCustomerChannel = Object.values(action.payload.customerChannels).some(
        (ch) => ch.stateHistory?.at(-1)?.state === 'Connected' || ch.stateHistory?.at(-1)?.state === 'Ringing',
      );

      return {
        ...state,
        call: action.payload,
        callType: state.callType || determineCallType(action.payload),
        dispositions: filterDispositions(state.exclusionLists, action.payload.dispositions),
        callbackEndpoint: cb?.endpoint ?? firstEndpoint,
        leadEndpoint: firstEndpoint,
        currentDispo: sameDispo ? state.currentDispo : matchedDispo ?? (isNewCall ? null : state.currentDispo),
        currentExclusionList: serverDispo?.exclusionList
          ? { value: serverDispo.exclusionList, label: serverDispo.exclusionList }
          : isNewCall
          ? null
          : state.currentExclusionList,
        canDispo: matchedDispo ? true : isNewCall ? false : state.canDispo,
        isInbound: Object.values(action.payload.customerChannels).some((ch) => ch.isInbound),
        canCallPreview: !hasActiveCustomerChannel,
        callbackToAgent: cb ? cb.forAgent === state.username : isNewCall ? false : state.callbackToAgent,
        callbackTime: cb?.scheduledLocalTime ?? (isNewCall ? null : state.callbackTime),
        callbackNotes: cb?.notes ?? (isNewCall ? '' : state.callbackNotes),
      };
    }

    case 'SET_AGENT_PRESENCE': {
      // Filter out the current user, reformat to array (handleAgentStates in App.svelte)
      const entries = Object.entries(action.payload)
        .filter(([k]) => k !== state.username)
        .map(([k, v]) => ({ value: k, label: k, status: v }));
      return { ...state, agentStates: entries };
    }

    case 'SET_AGENT_MUTED':
      return { ...state, agentMuted: action.payload };

    case 'SET_OFFERED_CALL':
      return { ...state, offeredCall: action.payload };

    case 'SET_PENDING_PREVIEW_CALL':
      return { ...state, pendingPreviewCall: action.payload };

    case 'SET_EXCLUSION_LISTS': {
      const newExclusionLists = action.payload.map((list) => ({ value: list, label: list }));
      return {
        ...state,
        exclusionLists: newExclusionLists,
        // Re-filter dispositions with updated exclusion lists
        ...(state.call && {
          dispositions: filterDispositions(newExclusionLists, state.call.dispositions),
        }),
      };
    }

    case 'SET_CURRENT_DISPO':
      return {
        ...state,
        currentDispo: action.payload,
        canDispo: action.payload !== null,
        // Clear exclusion list when dispo changes
        currentExclusionList: null,
      };

    case 'SET_CURRENT_EXCLUSION_LIST':
      return { ...state, currentExclusionList: action.payload };

    case 'SET_CALLBACK_ENDPOINT':
      return { ...state, callbackEndpoint: action.payload };

    case 'SET_CALLBACK_TO_AGENT':
      return { ...state, callbackToAgent: action.payload };

    case 'SET_CALLBACK_TIME':
      return { ...state, callbackTime: action.payload };

    case 'SET_CALLBACK_NOTES':
      return { ...state, callbackNotes: action.payload };

    case 'SET_NEW_LEAD_NAME':
      return { ...state, newLeadName: action.payload };

    case 'SET_NEW_LEAD_TIMEZONE':
      return { ...state, newLeadTimezone: action.payload };

    case 'SET_NEW_LEAD_EXTERNAL_ID':
      return { ...state, newLeadExternalId: action.payload };

    case 'SET_CURRENT_CLIENT_CODE':
      return { ...state, currentClientCode: action.payload };

    case 'SET_CURRENT_HEAR_ABOUT_US':
      return { ...state, currentHearAboutUs: action.payload };

    case 'SET_CURRENT_IS_TRANSFER':
      return { ...state, currentIsTransfer: action.payload };

    case 'SET_CURRENT_PHONE_NUMBER':
      return { ...state, currentPhoneNumber: action.payload };

    case 'SET_CURRENT_MEMBERSHIP_ID':
      return { ...state, currentMembershipID: action.payload };

    case 'SET_CURRENT_INSURANCE_OFFERED':
      return { ...state, currentInsuranceOffered: action.payload };

    case 'SET_CURRENT_INSURANCE_OUTCOME':
      return { ...state, currentInsuranceOutcome: action.payload };

    case 'SET_MANUAL_OUTBOUND_OPEN':
      return { ...state, manualOutboundOpen: action.payload };

    case 'SET_MANUAL_OUTBOUND_NUMBER':
      return { ...state, manualOutboundNumber: action.payload };

    case 'SET_MANUAL_OUTBOUND_TIMEZONE':
      return { ...state, manualOutboundTimezone: action.payload };

    case 'RESET_MANUAL_OUTBOUND':
      return {
        ...state,
        manualOutboundOpen: false,
        manualOutboundNumber: '',
        manualOutboundTimezone: null,
      };

    case 'SET_TIMEZONE_LIST':
      return { ...state, timezoneList: action.payload };

    default:
      return state;
  }
};
